import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Saved, entities, enums, formatters } from "@fraction/shared";
import { formatDistanceToNow } from "date-fns";
import _ from "lodash";
import { EyeIcon, Trash2Icon } from "lucide-react";
import { forwardRef, useCallback } from "react";
import {
  useDeleteDocument,
  useDocumentsQuery,
  usePreviewDocument,
} from "src/apps/PostFundedDashboard/queries";
import { Skeleton } from "src/components";
import { DocumentApprovalButtons } from "src/components/DocumentApproval";
import { Button } from "src/components/ui/button";
import { StatusIconWithText } from "src/embeds/Classifier/components/LabeledDocumentsList/LabeledDocumentItem";
import { useModal } from "src/hooks";

export type DocumentPermissions = "delete" | "status-change";

export const ExistingDocumentList = ({
  applicationId,
  enabled,
  draggable,
  mutatable,
  permissions,
}: {
  applicationId?: string;
  enabled?: boolean;
  draggable?: boolean;
  mutatable?: boolean;
  permissions?: DocumentPermissions[];
}) => {
  const { data, isLoading } = useDocumentsQuery(applicationId, { enabled });

  const { showModal, closeModal } = useModal();

  const { deleting, mutate: deleteDocument } = useDeleteDocument({
    applicationId,
    onSuccess: () => {
      closeModal?.();
    },
  });

  const handleClickDelete = useCallback((document: Saved<entities.UploadedFile>) => {
    showModal({
      closeButton: true,
      closeOnClickBackdrop: true,
      message: (
        <>
          Are you sure you want to delete <b>{document?.type?.fullName}</b>?
        </>
      ),
      actions: [
        {
          text: "Cancel",
          type: "inverse",
          action: () => {
            closeModal();
          },
        },
        {
          text: "Delete",
          type: "urgent",
          action: () => {
            closeModal();
            deleteDocument(document.id);
          },
        },
      ],
    });
  }, []);

  const handleClickView = usePreviewDocument();

  if (!applicationId) {
    return null;
  }

  const sorted = _.sortBy(data, (doc) => doc.updatedAt).reverse();
  const needsWorkDocs = sorted?.filter(
    (doc) => doc.status === enums.ApplicationTaskApprovalStatus.NEEDS_WORK
  );
  const otherDocs = sorted?.filter((doc) => doc.status !== enums.ApplicationTaskApprovalStatus.NEEDS_WORK);
  const docs = [...(needsWorkDocs || []), ...(otherDocs || [])];

  return (
    <div className="bg-gray-100 h-full rounded p-3 flex flex-col gap-2 overflow-y-scroll overflow-x-hidden overscroll-contain">
      {isLoading
        ? Array.from({ length: 20 }).map((_, i) => <Skeleton className="h-[138px]" key={i} />)
        : null}
      {docs?.map((document) => (
        <ExistingDocumentItem
          draggable={draggable}
          key={document.id}
          document={document}
          permissions={permissions}
          onClickDelete={handleClickDelete}
          onClickView={handleClickView}
          deleting={deleting === document.id}
          mutatable={mutatable}
        />
      ))}
    </div>
  );
};

export const ExistingDocumentItem = ({
  document,
  onClickDelete,
  onClickView,
  deleting,
  draggable,
  mutatable,
  permissions,
}: {
  document: Saved<entities.UploadedFile>;
  onClickDelete?: (doc: Saved<entities.UploadedFile>) => void;
  onClickView?: (doc: Saved<entities.UploadedFile>) => void;
  deleting?: boolean;
  draggable?: boolean;
  mutatable?: boolean;
  permissions?: DocumentPermissions[];
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    disabled: !draggable,
    id: document.id,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const draggableAttributes = draggable ? attributes : {};

  return (
    <ExistingDocumentItemPresentational
      document={document}
      ref={setNodeRef}
      style={style}
      onClickDelete={onClickDelete}
      onClickView={onClickView}
      deleting={deleting}
      mutatable={mutatable}
      permissions={permissions}
      {...listeners}
      {...draggableAttributes}
    />
  );
};

export const ExistingDocumentItemPresentational = forwardRef(
  (
    {
      document,
      onClickDelete,
      onClickView,
      deleting,
      mutatable,
      permissions,
      ...props
    }: {
      document: Saved<entities.UploadedFile>;
      onClickDelete?: (doc: Saved<entities.UploadedFile>) => void;
      onClickView?: (doc: Saved<entities.UploadedFile>) => void;
      deleting?: boolean;
      mutatable?: boolean;
      permissions?: DocumentPermissions[];
      style?: any;
    },
    ref
  ) => {
    if (!document) {
      return null;
    }

    const handleClickDelete = useCallback(() => {
      onClickDelete?.(document);
    }, [onClickDelete, document?.id]);

    const handleClickView = useCallback(() => {
      onClickView?.(document);
    }, [onClickView, document?.id]);

    return (
      <div
        // @ts-ignore
        ref={ref}
        className="border rounded border-gray-300 p-2 gap-2 flex flex-col bg-white justify-center"
        key={document.id}
        {...props}
      >
        <div className="flex flex-row items-center justify-between gap-6">
          <div className="flex flex-row items-center gap-6">
            <div className="flex flex-col gap-1">
              <p
                title={`${document.type?.fullName} ${
                  document.applicants?.length
                    ? `for ${document.applicants
                        .map((applicant: entities.ApplicantT) =>
                          applicant?.user ? formatters.user.userName(applicant?.user) : ""
                        )
                        .join(", ")}`
                    : ""
                }
              ${
                document.debts?.length
                  ? ` for ${document.debts
                      .map((debt: entities.Debt) => formatters.debt.formatDebtName(debt))
                      .join(", ")}`
                  : ""
              }`}
                className="text-sm"
              >
                <b>{document.type?.fullName}</b>{" "}
                {document.applicants?.length
                  ? `for ${document.applicants
                      .map((applicant: entities.ApplicantT) =>
                        applicant?.user ? formatters.user.userName(applicant?.user) : ""
                      )
                      .join(", ")}`
                  : ""}
                {document.debts?.length
                  ? ` for ${document.debts
                      .map((debt: entities.Debt) => formatters.debt.formatDebtName(debt))
                      .join(", ")}`
                  : ""}
                {document.incomes?.length
                  ? ` for ${document.incomes
                      .map((income: entities.Income) => formatters.ownables.formatIncomeName(income))
                      .join(", ")}`
                  : ""}
              </p>
              <p className="text-xs text-gray-500">
                Uploaded by{" "}
                {document.source
                  ? _.startCase(document.source)
                  : document?.uploader
                  ? formatters.user.userName(document?.uploader)
                  : "Fraction"}
              </p>
              <StatusIconWithText
                className="mt-0.5"
                status={document.status || enums.ApplicationTaskApprovalStatus.REQUESTED}
              />
            </div>
          </div>

          <div className="flex flex-col items-end gap-y-2">
            <div className="flex flex-row gap-1 items-center">
              {(mutatable || permissions?.includes("delete")) && handleClickDelete ? (
                <Button
                  pendingWithIcon={deleting}
                  variant="outline:red"
                  size="sm"
                  onClick={handleClickDelete}
                >
                  Delete
                  <Trash2Icon height={16} width={16} className="ml-2 rounded" />
                </Button>
              ) : null}
              {onClickView && document?.s3Document?.href ? (
                <Button variant="outline" size="sm" onClick={handleClickView}>
                  Preview
                  <EyeIcon height={16} width={16} className="ml-2 rounded" />
                </Button>
              ) : null}
            </div>
            <div className="flex flex-row items-center gap-2">
              {document.createdAt ? (
                <p className="text-xs text-gray-500">
                  Uploaded {formatDistanceToNow(document.createdAt, { addSuffix: true })} on{" "}
                  {formatters.date.formatDate(document.date || document.createdAt)}
                </p>
              ) : null}
            </div>
            {mutatable || permissions?.includes("status-change") ? (
              <div className="flex flex-row justify-end gap-1 bg-white">
                <DocumentApprovalButtons file={document} />
              </div>
            ) : null}
          </div>
        </div>

        {document.notes ? (
          <p className="bg-gray-400 p-2 text-sm rounded">
            <b>Notes</b>
            <br />
            {document.notes}
          </p>
        ) : null}
      </div>
    );
  }
);
